// ScrollyCard.tsx — Phase 11 Step 3 Block 3
// Feed-Card für Scrollys. Source-of-truth: Scrolly Platform.html:1042–1108.
//
// Padding-Vertrag (wichtig!):
//   Die Quelle rendert **kein** Padding auf der Card — der Meta-Block sitzt direkt
//   unter dem Cover mit `marginTop: 10`. `CARD_STYLES[variant].cardPadding = 0` ist
//   damit die Quell-Wahrheit, nicht ein Default. Abgrenzung gegenüber dem Feed
//   trägt Shadow + borderRadius + Hover-Lift — nicht Inner-Space.
//   Falls Cards im Feed kleben: Lösung ist `CardGrid.gap` hochziehen
//   (Container-Space), nicht `card.padding` (Inner-Space).
//
// Variant-Kopplung:
//   Liest `useVariant()` intern und picked `CARD_STYLES[variant]`. Props bekommen
//   keine Farb-Tokens durchgereicht — die Variant entscheidet die Meta-Farben.
//
// Hover-Behavior (Quelle: Zeile 1051–1078):
//   - Shadow wechselt rest → hover.
//   - Cover-Wrapper bekommt `translateY(-2px)` (source schreibt genau diesen Wert).
//   - Cover-Overlay zeigt "Start scroll"-Pill mit blur(2px).
//   - `prefers-reduced-motion: reduce` deaktiviert die Lift-Transition
//     (globale Regel in `src/styles/global.css`).

import { useState, type CSSProperties } from "react";
import { Cover } from "./Cover";
import {
  IconCheckBadge,
  IconPlay,
  IconScroll,
} from "../common/Icons";
import { useVariant } from "../../hooks/useVariant";
import {
  CARD_STYLES,
  PINK,
  SOURCE_BADGE_STYLES,
  type VariantId,
} from "../../lib/tokens";
import { formatViews } from "../../lib/format";
import { CATEGORY_BY_ID } from "../../lib/categories";
import type { MockScrolly } from "../../lib/mock/scrollys";

// ---- Props --------------------------------------------------------------

export type ScrollyCardProps = {
  scrolly: MockScrolly;
  /** Aspect-Ratio des Covers. Default `3/4`. `9/16` für Phone-Story-Layout. */
  aspect?: "3/4" | "9/16";
};

// ---- Component ----------------------------------------------------------

export function ScrollyCard({ scrolly, aspect = "3/4" }: ScrollyCardProps) {
  const { variant } = useVariant();
  const style = CARD_STYLES[variant];
  const [hovered, setHovered] = useState(false);

  const categoryLabel =
    CATEGORY_BY_ID[scrolly.categoryId]?.label ?? scrolly.categoryId;
  const viewsLabel = formatViews(scrolly.stats.views);
  // Source normalisiert `duration: "… scroll"` via `.replace(' scroll', '')`.
  // Unsere Mocks schreiben "4 Min Scroll" (dt.) — wir kappen das Suffix analog.
  const durationLabel = scrolly.stats.duration.replace(/ Scroll$/i, "");

  // Phase 11.5 — Source-Badge-Label.
  // - embed:    "via {creator.name}" (Publisher ist der Creator selbst).
  // - imported: "via {scrolly.importedFrom}" (Creator ist Native, Quelle separat).
  // - native:   kein Badge.
  const sourceLabel = getSourceBadgeLabel(scrolly);

  // Phase 11 Step 4 Block 2 — Routing:
  //   Cover-Link → /s/:slug (Viewer), Creator-Chip → /@:handle (Profile).
  //   Nested-Anchor-Regeln zwingen zur Aufteilung — ein Outer-Link wäre
  //   HTML-invalid wenn darin ein zweites <a> steckt. Wir trennen daher
  //   Cover-Anchor und Creator-Anchor im DOM (Cover oben, Meta-Row mit
  //   Creator-Link unten). Views + Duration bleiben nicht-klickbar.
  const viewerHref = `/s/${scrolly.slug}`;
  const creatorHref = `/@${scrolly.creator.handle}`;

  return (
    <article
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: style.cardPadding,
        // Transition bleibt auf dem Wrapper, die tatsächliche Translate passiert
        // auf dem Cover-Wrapper (siehe unten) — kongruent zur Quelle.
      }}
    >
      <a
        href={viewerHref}
        aria-label={`${scrolly.title} — ${scrolly.creator.name}`}
        style={{
          display: "block",
          color: "inherit",
          textDecoration: "none",
          cursor: "pointer",
        }}
      >
        <CoverShell
          hovered={hovered}
          style={style}
          aspect={aspect}
          variant={variant}
          sourceLabel={sourceLabel}
          coverNode={
            <Cover
              palette={scrolly.cover.palette}
              decoration={scrolly.cover.decoration}
              category={categoryLabel}
              title={scrolly.title}
              creator={scrolly.creator}
              aspect={aspect}
              bigNumberValue={scrolly.cover.bigNumberValue}
            />
          }
        />
      </a>

      {/* Meta-Block — Source: Zeile 1082–1105.
          Abstand Cover → Meta: `style.metaGap` (source: marginTop: 10).
          Liegt ausserhalb des Cover-Anchors, damit der Creator-Chip ein
          eigener Link zu /@:handle sein kann (HTML verbietet nested <a>). */}
      <div
        style={{
          display: "flex",
          gap: 10,
          marginTop: style.metaGap,
          alignItems: "center",
        }}
      >
        <a
          href={creatorHref}
          aria-label={`Profil von ${scrolly.creator.name}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flex: 1,
            minWidth: 0,
            color: "inherit",
            textDecoration: "none",
            cursor: "pointer",
          }}
        >
          <Avatar name={scrolly.creator.name} color={scrolly.creator.avatar} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: style.metaText,
                display: "flex",
                alignItems: "center",
                gap: 4,
                // Name kann lang werden — Truncate, damit Stats nicht umbrechen.
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {scrolly.creator.name}
              </span>
              <IconCheckBadge size={11} />
            </div>
            <div
              style={{
                fontSize: 12,
                color: style.metaMuted,
                marginTop: 1,
              }}
            >
              {viewsLabel} Scrolls · {scrolly.publishedAt}
            </div>
          </div>
        </a>
        <div
          style={{
            fontSize: 11,
            color: style.metaMuted,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 3,
            flexShrink: 0,
          }}
        >
          <IconScroll size={11} /> {durationLabel}
        </div>
      </div>
    </article>
  );
}

// ---- Cover-Shell --------------------------------------------------------
// Wrapper um das Cover, trägt Shadow + Hover-Lift + "Start scroll"-Overlay.
// Eigener Sub-Component, damit der ScrollyCard-Body nicht überquillt.

function CoverShell({
  hovered,
  style,
  aspect,
  variant,
  sourceLabel,
  coverNode,
}: {
  hovered: boolean;
  style: (typeof CARD_STYLES)["editorial"];
  aspect: "3/4" | "9/16";
  variant: VariantId;
  sourceLabel: string | null;
  coverNode: React.ReactNode;
}) {
  // Aspect wird intern vom Cover (CoverFrame) gesetzt — der Shell adaptiert
  // sich über sein Kind. Explizit keinen Wert setzen, sonst doppelte Quelle.
  void aspect;

  const shellStyle: CSSProperties = {
    position: "relative",
    borderRadius: style.coverRadius,
    overflow: "hidden",
    background: style.coverBg,
    boxShadow: hovered ? style.shadowHover : style.shadowRest,
    transform: hovered ? "translateY(-2px)" : "translateY(0)",
    // Transitionen werden global.css' `@media (prefers-reduced-motion: reduce)`
    // auf 0s gedrückt — reduced-motion-User sehen Shadow+Lift als Jump statt Slide.
    transition: "box-shadow 180ms, transform 180ms",
    willChange: hovered ? "transform" : undefined,
  };

  return (
    <div style={shellStyle} data-scrolly-card-cover="">
      {coverNode}
      {sourceLabel && <SourceBadge variant={variant} label={sourceLabel} />}
      {hovered && <StartScrollOverlay />}
    </div>
  );
}

// ---- Source-Badge -------------------------------------------------------
// Phase 11.5. Dezentes "via {Publisher}"-Chip unten-rechts auf dem Cover.
// Nur gerendert wenn `source !== "native"`. Variant-aware über
// `SOURCE_BADGE_STYLES` (lib/tokens.ts). Sitzt **unter** dem Start-Scroll-
// Overlay in der DOM-Order — auf Hover überdeckt das Overlay den Badge,
// was beabsichtigt ist (Hover = Aktion, Badge = Herkunft).

function SourceBadge({
  variant,
  label,
}: {
  variant: VariantId;
  label: string;
}) {
  const s = SOURCE_BADGE_STYLES[variant];
  return (
    <div
      // `data-source-badge` dient als Hook für Devview-Screenshots & späteren
      // Selektor in Tests. Kein CSS-Styling darüber.
      data-source-badge=""
      style={{
        position: "absolute",
        right: s.offset,
        bottom: s.offset,
        background: s.bg,
        color: s.color,
        border: s.border,
        fontSize: s.fontSize,
        fontWeight: 600,
        letterSpacing: 0.2,
        padding: s.padding,
        borderRadius: s.radius,
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        whiteSpace: "nowrap",
        maxWidth: "calc(100% - 20px)",
        overflow: "hidden",
        textOverflow: "ellipsis",
        lineHeight: 1.3,
      }}
    >
      {label}
    </div>
  );
}

/**
 * Label-Regel für den Source-Badge auf der ScrollyCard.
 *   - `embed`    → via {creator.name} (Creator ist der externe Publisher).
 *   - `imported` → via {scrolly.importedFrom} (Creator bleibt Native, Quelle separat).
 *   - `native`   → null (kein Badge).
 */
function getSourceBadgeLabel(scrolly: MockScrolly): string | null {
  if (scrolly.source === "embed") {
    return `via ${scrolly.creator.name}`;
  }
  if (scrolly.source === "imported" && scrolly.importedFrom) {
    return `via ${scrolly.importedFrom}`;
  }
  return null;
}

function StartScrollOverlay() {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
      }}
      aria-hidden="true"
    >
      <div
        style={{
          background: "rgba(255,255,255,0.95)",
          color: "#111",
          padding: "10px 18px",
          borderRadius: 999,
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 13.5,
          fontWeight: 600,
        }}
      >
        <IconPlay size={14} />
        Start scroll
      </div>
    </div>
  );
}

// ---- Avatar -------------------------------------------------------------
// Source: Scrolly Platform.html:808–821.
// Farb-Token (Hex) oder später URL — aktuell rendern wir immer die Initialen
// auf farbigem Kreis. Wenn `avatar` eine URL wird, hier eine `background-image`-
// Variante ergänzen.

function Avatar({
  name,
  color,
  size = 28,
}: {
  name: string;
  color: string;
  size?: number;
}) {
  const initial =
    name
      .split(" ")
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: color || PINK,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontWeight: 600,
        fontSize: size * 0.42,
        flexShrink: 0,
        letterSpacing: -0.5,
        fontFamily: "inherit",
      }}
      aria-hidden="true"
    >
      {initial}
    </div>
  );
}
